import api from './api';

export interface EventParticipant {
    id: number;
    zalo_name: string;
    phone?: string; // Only visible to admin
    is_returning_customer: boolean;
    chosen_numbers?: string[];
    fb_screenshot?: string;
    youtube_screenshot?: string;
    admin_verified_fb?: boolean;
    admin_verified_youtube?: boolean;
    prize_type?: 'first' | 'second' | 'third' | 'encouragement' | null;
    user_id?: number;
    submitted_at: string;
    created_at?: string;
    updated_at?: string;
    user?: {
        id: number;
        username: string;
        full_name?: string;
    };
}

export interface EventConfig {
    id: number;
    event_name: string;
    event_description: string;
    fb_post_link?: string;
    youtube_video_link?: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface EventParticipantListResponse {
    participants: EventParticipant[];
    total: number;
    page: number;
    per_page: number;
}

export interface EventStats {
    total_participants: number;
    returning_customers: number;
    new_customers: number;
    fb_verified: number;
    youtube_verified: number;
    has_fb_screenshot: number;
    has_youtube_screenshot: number;
}

class EventService {
    // ============== PUBLIC APIs ==============

    async getConfig(): Promise<{ config: EventConfig }> {
        const response = await api.get('/events/config');
        return response.data;
    }

    async register(data: {
        zalo_name: string;
        phone: string;
        is_returning_customer: boolean;
        chosen_numbers: string[];
    }): Promise<{ message: string; participant: EventParticipant }> {
        const response = await api.post('/events/register', data);
        return response.data;
    }

    async getPublicParticipants(params: {
        page?: number;
        per_page?: number;
    }): Promise<EventParticipantListResponse> {
        const response = await api.get('/events/participants/public', { params });
        return response.data;
    }

    async checkNumber(number: string): Promise<{
        is_taken: boolean;
        taken_by?: string;
        taken_at?: string;
    }> {
        const response = await api.get(`/events/check-number/${number}`);
        return response.data;
    }

    async uploadScreenshot(
        participantId: number,
        type: 'fb' | 'youtube',
        file: File
    ): Promise<{ message: string; path: string }> {
        const formData = new FormData();
        formData.append('participant_id', participantId.toString());
        formData.append('type', type);
        formData.append('file', file);

        const response = await api.post('/events/upload-screenshot', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }

    async getMyParticipation(): Promise<{
        has_participated: boolean;
        participant?: EventParticipant;
    }> {
        const response = await api.get('/events/my-participation');
        return response.data;
    }

    // ============== ADMIN APIs ==============

    async adminListParticipants(params: {
        page?: number;
        per_page?: number;
        search?: string;
        verified?: 'fb' | 'youtube' | 'both' | 'none';
        prize?: string;
    }): Promise<EventParticipantListResponse> {
        const response = await api.get('/events/admin/participants', { params });
        return response.data;
    }

    async adminVerify(
        id: number,
        data: {
            admin_verified_fb?: boolean;
            admin_verified_youtube?: boolean;
        }
    ): Promise<{ message: string; participant: EventParticipant }> {
        const response = await api.put(`/events/admin/verify/${id}`, data);
        return response.data;
    }

    async adminSetPrize(
        id: number,
        prize_type: string | null
    ): Promise<{ message: string; participant: EventParticipant }> {
        const response = await api.put(`/events/admin/set-prize/${id}`, {
            prize_type,
        });
        return response.data;
    }

    async adminDeleteParticipant(id: number): Promise<{ message: string }> {
        const response = await api.delete(`/events/admin/participant/${id}`);
        return response.data;
    }

    async adminBulkDelete(ids: number[]): Promise<{
        message: string;
        deleted_count: number;
    }> {
        const response = await api.post('/events/admin/bulk-delete', { ids });
        return response.data;
    }

    async adminGetConfig(): Promise<{ config: EventConfig }> {
        const response = await api.get('/events/admin/config');
        return response.data;
    }

    async adminUpdateConfig(
        data: Partial<EventConfig>
    ): Promise<{ message: string; config: EventConfig }> {
        const response = await api.put('/events/admin/config', data);
        return response.data;
    }

    async adminGetStats(): Promise<EventStats> {
        const response = await api.get('/events/admin/stats');
        return response.data;
    }
}

export const eventService = new EventService();
